import { useEffect, useState, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";

// Pages
import Landing from "@/pages/Landing";
import HomePage from "@/pages/HomePage";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import PersonalizationSummary from "@/pages/PersonalizationSummary";
import FirstMealScreen from "@/pages/FirstMealScreen";
import Dashboard from "@/pages/Dashboard";
import EnhancedMealAnalysis from "@/pages/EnhancedMealAnalysis";
import GlucoseLog from "@/pages/GlucoseLog";
import Insights from "@/pages/Insights";
import Chat from "@/pages/Chat";
import AuthCallback from "@/pages/AuthCallback";
import Profile from "@/pages/Profile";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import SubscriptionCancel from "@/pages/SubscriptionCancel";
import MealJournal from "@/pages/patient/MealJournal";
import JoinProgram from "@/pages/JoinProgram";
import ConsultationBooking from "@/pages/ConsultationBooking";
import ProgrammeLanding from "@/pages/ProgrammeLanding";
import ProgrammeRegister from "@/pages/ProgrammeRegister";
import ManageAppointment from "@/pages/ManageAppointment";
import MyPlanSwap from "@/pages/MyPlanSwap";
import LeaveReview from "@/pages/LeaveReview";
import ReviewsPage from "@/pages/staff/ReviewsPage";
import CampaignJoin from "@/pages/CampaignJoin";
import UnsubscribePage from "@/pages/UnsubscribePage";

// Staff Dashboard Pages (Unified)
import StaffLogin from "@/pages/staff/StaffLogin";
import StaffLayout from "@/pages/staff/StaffLayout";
import StaffOverview from "@/pages/staff/StaffOverview";
import StaffPatients from "@/pages/staff/StaffPatients";
import StaffPatientProfile from "@/pages/staff/PatientProfile";
// Staff Dashboard Section Pages (Real implementations)
import ConsultationsPage from "@/pages/staff/ConsultationsPage";
import AssessmentsPage from "@/pages/staff/AssessmentsPage";
import MealPlansPage from "@/pages/staff/MealPlansPage";
import MealPlanDetail from "@/pages/staff/MealPlanDetail";
import ReportsPage from "@/pages/staff/ReportsPage";
import FollowUpsPage from "@/pages/staff/FollowUpsPage";
import ProgrammePage from "@/pages/staff/ProgrammePage";
import DoctorCommsPage from "@/pages/staff/DoctorCommsPage";
import CommunicationsPage from "@/pages/staff/CommunicationsPage";
import IntegrationsPage from "@/pages/staff/IntegrationsPage";
import NewslettersPage from "@/pages/staff/NewslettersPage";
import ResourcesPage from "@/pages/staff/ResourcesPage";
import SubscribersPage from "@/pages/staff/SubscribersPage";
import SettingsPage from "@/pages/staff/SettingsPage";

// Legacy Admin Pages (preserved, accessible through unified dashboard)
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminMarketplace from "@/pages/admin/AdminMarketplace";
import AdminPrivatePhysicians from "@/pages/admin/AdminPrivatePhysicians";
import AdminCorporate from "@/pages/admin/AdminCorporate";

// Dietitian Pages
import DietitianLayout from "@/pages/dietitian/DietitianLayout";
import DietitianPatients from "@/pages/dietitian/DietitianPatients";
import DietitianLeads from "@/pages/dietitian/DietitianLeads";
import PatientProfile from "@/pages/dietitian/PatientProfile";
import WeeklySummaries from "@/pages/dietitian/WeeklySummaries";
import WellnessMealReview from "@/pages/dietitian/WellnessMealReview";
import DietitianLeaderboard from "@/pages/dietitian/DietitianLeaderboard";

// Partner Pages
import PartnerLayout from "@/pages/partner/PartnerLayout";
import PartnerOverview from "@/pages/partner/PartnerOverview";
import PartnerPatients from "@/pages/partner/PartnerPatients";
import PartnerPhysicians from "@/pages/partner/PartnerPhysicians";
import PartnerReports from "@/pages/partner/PartnerReports";
import PartnerSupport from "@/pages/partner/PartnerSupport";

// Corporate Wellness Pages
import CorporateLayout from "@/pages/corporate/CorporateLayout";
import CorporateOverview from "@/pages/corporate/CorporateOverview";
import CorporatePrograms from "@/pages/corporate/CorporatePrograms";
import CorporateLeaderboard from "@/pages/corporate/CorporateLeaderboard";
import CorporateEmployees from "@/pages/corporate/CorporateEmployees";

// Legal Pages
import { PrivacyPage, TermsPage, ContactPage } from "@/pages/LegalPages";

// Auth Pages
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

// Invitation Pages
import AcceptInvite from "@/pages/AcceptInvite";

// Marketplace Pages
import FindDietitian from "@/pages/FindDietitian";

// Meal Plan Pages
import MealPlanPage from "@/pages/MealPlanPage";
import IntakeForm from "@/pages/IntakeForm";
import FoodPreferenceForm from "@/pages/FoodPreferenceForm";
import CheckInForm from "@/pages/CheckInForm";

// Messaging Pages
import MessagesPage from "@/pages/MessagesPage";

// Patient Self-Service Pages
import MyMealPlans from "@/pages/patient/MyMealPlans";
import MyMealPlanDetail from "@/pages/patient/MyMealPlanDetail";
import PostOnboardingPrompt from "@/pages/PostOnboardingPrompt";
import CareTeamDetails from "@/pages/patient/CareTeamDetails";

// PWA Components

// Use the current domain if REACT_APP_BACKEND_URL is not set or empty
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.trim() || window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const { setLanguageFromProfile } = useLanguage();

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
        
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Only set language from user profile if no localStorage preference exists
        const savedLang = localStorage.getItem('platemind_language');
        if (!savedLang && userData.preferred_language) {
          setLanguageFromProfile(userData.preferred_language);
        }
      } else {
        localStorage.removeItem("token");
        setToken(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("token");
      setToken(null);
    }
    setLoading(false);
  }, [setLanguageFromProfile]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (userData, authToken) => {
    setToken(authToken);
    localStorage.setItem("token", authToken);
    
    // Fetch full user data from /auth/me to get all fields including has_care_team
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
        
      });
      if (response.ok) {
        const fullUserData = await response.json();
        setUser(fullUserData);
        // Only set language from user profile if no localStorage preference exists
        const savedLang = localStorage.getItem('platemind_language');
        if (!savedLang && fullUserData.preferred_language) {
          setLanguageFromProfile(fullUserData.preferred_language);
        }
        return;
      }
    } catch (error) {
      console.error("Failed to fetch full user data:", error);
    }
    
    // Fallback to partial user data if /auth/me fails
    setUser(userData);
    // Only set language from user profile if no localStorage preference exists
    const savedLang = localStorage.getItem('platemind_language');
    if (!savedLang && userData.preferred_language) {
      setLanguageFromProfile(userData.preferred_language);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) return;

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error("User refresh failed:", error);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, logout, checkAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/admin", { state: { from: location } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return children;
};

// Staff Gate - shows login if not auth, redirects to overview if auth
const StaffGate = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center"><div className="animate-pulse text-stone-400">Loading...</div></div>;
  if (user) return <Navigate to="/admin/overview" replace />;
  return <StaffLogin />;
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/legacy" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/join/:code" element={<JoinProgram />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/consultation" element={<ConsultationBooking />} />
      <Route path="/programme" element={<ProgrammeLanding />} />
      <Route path="/programme/register" element={<ProgrammeRegister />} />
      <Route path="/appointment/:token" element={<ManageAppointment />} />
      <Route path="/my-plan/:token" element={<MyPlanSwap />} />
      <Route path="/review/:token" element={<LeaveReview />} />
      <Route path="/event/:slug" element={<CampaignJoin />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <PostOnboardingPrompt />
          </ProtectedRoute>
        }
      />
      <Route
        path="/personalization"
        element={
          <ProtectedRoute>
            <PersonalizationSummary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/first-meal"
        element={
          <ProtectedRoute>
            <FirstMealScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meal-analysis"
        element={
          <ProtectedRoute>
            <EnhancedMealAnalysis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/glucose"
        element={
          <ProtectedRoute>
            <GlucoseLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <Insights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meal-journal"
        element={
          <ProtectedRoute>
            <MealJournal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/cancel"
        element={
          <ProtectedRoute>
            <SubscriptionCancel />
          </ProtectedRoute>
        }
      />
      
      {/* Staff Dashboard - /admin shows login if not auth, redirects to overview if auth */}
      <Route path="/admin" element={<StaffGate />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <StaffLayout />
          </ProtectedRoute>
        }
      >
        <Route path="overview" element={<StaffOverview />} />
        <Route path="consultations" element={<ConsultationsPage />} />
        <Route path="patients" element={<StaffPatients />} />
        <Route path="patients/:patientId" element={<StaffPatientProfile />} />
        <Route path="assessments" element={<AssessmentsPage />} />
        <Route path="meal-plans" element={<MealPlansPage />} />
        <Route path="meal-plans/:planId" element={<MealPlanDetail />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="follow-ups" element={<FollowUpsPage />} />
        <Route path="programme" element={<ProgrammePage />} />
        <Route path="doctor-comms" element={<DoctorCommsPage />} />
        <Route path="communications" element={<CommunicationsPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="newsletters" element={<NewslettersPage />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="subscribers" element={<SubscribersPage />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Legacy Admin Routes (preserved for backward compatibility) */}
      <Route
        path="/legacy-admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="private-physicians" element={<AdminPrivatePhysicians />} />
        <Route path="marketplace" element={<AdminMarketplace />} />
        <Route path="corporate" element={<AdminCorporate />} />
        <Route path="notifications" element={<AdminNotifications />} />
      </Route>

      {/* Physician Routes */}
      <Route
        path="/physician"
        element={
          <ProtectedRoute>
            <DietitianLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DietitianPatients />} />
        <Route path="weekly-summaries" element={<WeeklySummaries />} />
        <Route path="leads" element={<DietitianLeads />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="patient/:patientId" element={<PatientProfile />} />
        <Route path="wellness" element={<WellnessMealReview />} />
        <Route path="leaderboard" element={<DietitianLeaderboard />} />
      </Route>

      {/* Partner Routes */}
      <Route
        path="/partner"
        element={
          <ProtectedRoute>
            <PartnerLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PartnerOverview />} />
        <Route path="patients" element={<PartnerPatients />} />
        <Route path="patient/:patientId" element={<PatientProfile />} />
        <Route path="physicians" element={<PartnerPhysicians />} />
        <Route path="reports" element={<PartnerReports />} />
        <Route path="support" element={<PartnerSupport />} />
      </Route>

      {/* Corporate Wellness Dashboard */}
      <Route
        path="/corporate"
        element={
          <ProtectedRoute>
            <CorporateLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CorporateOverview />} />
        <Route path="programs" element={<CorporatePrograms />} />
        <Route path="leaderboard" element={<CorporateLeaderboard />} />
        <Route path="employees" element={<CorporateEmployees />} />
      </Route>

      {/* Public Pre-consultation Intake Form */}
      <Route path="/intake/:token" element={<IntakeForm />} />
      {/* Public Food Preference Form */}
      <Route path="/food-preferences/:formId" element={<FoodPreferenceForm />} />
      {/* Public Weekly Check-in Form */}
      <Route path="/checkin/:token" element={<CheckInForm />} />

      {/* Legal Pages (Public) */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Professional Login Shortcut */}
      <Route path="/professional" element={<Navigate to="/login?type=professional" replace />} />
      
      {/* Invitation Pages (Public) */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      
      {/* Find a Healthcare Provider Marketplace (B2C only) */}
      <Route path="/find-provider" element={
        <ProtectedRoute>
          <FindDietitian />
        </ProtectedRoute>
      } />
      
      {/* Meal Plan Page (Patient) */}
      <Route path="/meal-plan" element={
        <ProtectedRoute>
          <MealPlanPage />
        </ProtectedRoute>
      } />

      {/* Messages Page */}
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/messages/:recipientId" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />

      {/* B2C Meal Plans (Self-Service) */}
      <Route path="/my-meal-plans" element={
        <ProtectedRoute>
          <MyMealPlans />
        </ProtectedRoute>
      } />
      <Route path="/my-meal-plans/:planId" element={
        <ProtectedRoute>
          <MyMealPlanDetail />
        </ProtectedRoute>
      } />
      <Route path="/care-team" element={
        <ProtectedRoute>
          <CareTeamDetails />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRouter />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
